// NUMA · CAPACITY — specialist. Reads the shared FLO snapshot (useTodayData) and
// formats it exactly like olmps/orderWidgets/NumaWidget.tsx: Charge to one decimal
// (0–10), Load as a rounded whole number, Flow as a rounded score. No percentages.
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel } from './util';

const BASE = { id: 'numa', toolId: 'numa', orderFunction: 'regulate', title: 'CAPACITY', deityName: 'NUMA', agentId: 'numa' } as const;

// Mirrors v10/useCurrentStep.ts `paceOf` (not exported there): load at/above a typical
// day (~700) is strained; charge below 4 is drained; below the green band is strained.
const HIGH_LOAD = 700;
const DRAINED_CHARGE = 4;

export function useNumaWidget(c: WidgetCtx): OrbWidgetModel {
  const flo = c.today.flo;
  if (!flo) return emptyModel(BASE, c, 'No capacity reading yet');

  const charge = flo.charge;
  const load = Math.round(flo.load);
  const flow = Math.round(flo.flow.score);
  const below = c.today.chargeBelowGreen;

  const drained = charge < DRAINED_CHARGE;
  const overloaded = flo.load >= HIGH_LOAD;
  const strained = !drained && (below || overloaded);

  const status: OrbWidgetModel['status'] = drained ? 'critical' : strained ? 'attention' : 'active';
  const lines: string[] = [];
  if (drained || overloaded) lines.push('You are carrying more than today’s capacity.');
  else if (below) lines.push('Charge is below your green band.');

  return {
    ...BASE,
    status,
    summary: `Charge ${charge.toFixed(1)} · Load ${load} · Flow ${flow}`,
    metrics: [
      { label: 'Charge', value: charge.toFixed(1), tone: drained ? 'critical' : below ? 'warning' : 'positive' },
      { label: 'Load', value: load, tone: overloaded ? 'warning' : 'neutral' },
      { label: 'Flow', value: flow },
    ],
    alert: drained ? 'Charge is drained' : strained ? (overloaded ? 'Load is high' : 'Charge below green') : null,
    lines,
    primaryAction: { label: 'Rebalance', run: () => c.openTool('numa'), tone: drained || strained ? 'gold' : 'ghost' },
    signals: { active: true, alerted: drained || strained },
  };
}
